import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { normalizeCapabilities } from '@rsos/capstone';
import { JURISDICTION_VIEW } from '@rsos/capstone/src/constants/capabilities';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { fetchPsapInfo } from '../capstoneAPI';

interface ActiveCapability {
  category: number;
  display_name: string;
  name: string;
}

interface MapLayerSettings {
  id: number;
  default_checked: boolean;
  opacity?: number;
}

export interface PSAPInfo {
  id?: number;
  name?: string;
  display_name?: string;
  account_id?: string;
  agency_id: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  state?: string;
  activeCapabilities: Record<string, ActiveCapability>;
  active_capabilities: ActiveCapability[];
  map_layer_settings?: Record<number, MapLayerSettings>;
}

export interface PsapState {
  loading: boolean;
  hasErrors: false | string;
  currentPSAP: PSAPInfo;
}

export interface StateWithPsaps {
  psaps: PsapState;
}

/* this works like an action, the creators are automatically produced thanks to createAsyncThunk */
/* they are listed in extra reducers */
export const fetchPsap = createAsyncThunk<PSAPInfo, string>(
  'psap/fetchPsapInfo',
  async (endpoint, { rejectWithValue }) => {
    try {
      const response = await fetchPsapInfo(endpoint);
      return response.data;
    } catch (err) {
      const { message } = normalizeError(err);
      return rejectWithValue(message);
    }
  },
);

export const initialState: PsapState = {
  loading: false,
  hasErrors: false,
  // Most of the codebase makes the assumption that currentPSAP is present
  // and
  currentPSAP: {} as unknown as PSAPInfo,
};

const psapInfo = createSlice({
  name: 'psapInfo',
  initialState,
  reducers: {
    /**
     * WARNING: This should only be used by the debug panel! It does not
     * actually grant a capability to an organization, it only displays the UI
     * as if the capability had been granted.
     */
    setCapability(
      state,
      {
        payload,
      }: {
        payload: {
          name: string;
          value: ActiveCapability | null;
        };
      },
    ) {
      if (payload.value) {
        state.currentPSAP.activeCapabilities[payload.name] = payload.value;
        state.currentPSAP.active_capabilities.push(payload.value);
      } else {
        delete state.currentPSAP.activeCapabilities[payload.name];
        state.currentPSAP.active_capabilities =
          state.currentPSAP.active_capabilities.filter(
            x => x.name != payload.name,
          );
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPsap.fulfilled, (state, action) => {
        state.currentPSAP = {
          ...action.payload,
          activeCapabilities: normalizeCapabilities(
            action.payload.active_capabilities,
          ) as Record<string, ActiveCapability>,
        };
        state.loading = false;
        state.hasErrors = false;
      })
      .addCase(fetchPsap.pending, state => {
        state.loading = true;
      })
      .addCase(fetchPsap.rejected, (state, action) => {
        state.loading = false;
        state.hasErrors = action.payload as string;
      });
  },
});

export const psapSelector = (state: StateWithPsaps) => state.psaps;
export const mapLayerSettingsSelector = (state: StateWithPsaps) =>
  state.psaps.currentPSAP?.map_layer_settings || {};
export const selectActiveCapabilities = (state: StateWithPsaps) =>
  state.psaps.currentPSAP?.activeCapabilities || {};
export const selectIsJVEnabled = createSelector(
  selectActiveCapabilities,
  activeCapabilities => Boolean(activeCapabilities[JURISDICTION_VIEW]),
);
export const { setCapability } = psapInfo.actions;
export const psaps = psapInfo.reducer;
export default psaps;
